import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useApp } from "../context/AppContext";
import { CATEGORIES } from "../data/mockData";
import { Package, ArrowRight, LockKeyhole } from "lucide-react";

const COLOR_MAP = {
  blue: {
    bg: "bg-blue-500/10",
    icon: "text-blue-400",
    border: "border-blue-500/20",
    dot: "bg-blue-400",
  },
  pink: {
    bg: "bg-pink-500/10",
    icon: "text-pink-400",
    border: "border-pink-500/20",
    dot: "bg-pink-400",
  },
  amber: {
    bg: "bg-amber-500/10",
    icon: "text-amber-400",
    border: "border-amber-500/20",
    dot: "bg-amber-400",
  },
  green: {
    bg: "bg-emerald-500/10",
    icon: "text-emerald-400",
    border: "border-emerald-500/20",
    dot: "bg-emerald-400",
  },
  purple: {
    bg: "bg-violet-500/10",
    icon: "text-violet-400",
    border: "border-violet-500/20",
    dot: "bg-violet-400",
  },
};

export default function Categories() {
  const { user } = useAuth();
  const { getProducts } = useApp();
  const navigate = useNavigate();
  const products = getProducts(user.id);

  const isMultiCategory = user.plan !== "Free";

  const categories = useMemo(
    () =>
      CATEGORIES.map((cat) => ({
        ...cat,
        count: products.filter((p) => p.category === cat.name).length,
      })),
    [products],
  );

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Plan notice */}
      {!isMultiCategory && (
        <div className="flex items-start gap-3 bg-amber-500/8 border border-amber-500/20 text-amber-400 rounded-2xl px-5 py-4">
          <LockKeyhole size={18} className="flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold">Free Plan: 1 Category Only</p>
            <p className="text-xs mt-0.5 text-amber-400/70">
              Upgrade to Advanced or Premium to access all market categories and
              list products in multiple segments.
            </p>
            <button
              onClick={() => navigate("/dashboard/pricing")}
              className="mt-2 text-xs font-semibold text-amber-400 hover:text-amber-300 underline underline-offset-2">
              Upgrade now →
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Market Categories
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Lagos-based product segments
          </p>
        </div>
        <span className="text-xs text-gray-500 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-3 py-1.5 rounded-xl">
          {categories.reduce((s, c) => s + c.count, 0)} total products
        </span>
      </div>

      {/* Category grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map((cat, idx) => {
          const locked = !isMultiCategory && idx > 0;
          const c = COLOR_MAP[cat.colorClass] || COLOR_MAP.purple;

          return (
            <div
              key={cat.id}
              className={`card p-5 transition-all duration-300 ${
                locked
                  ? "opacity-40 grayscale cursor-not-allowed"
                  : "hover:border-gray-300 dark:hover:border-gray-700 hover:shadow-card cursor-pointer hover:-translate-y-0.5"
              }`}
              onClick={() => !locked && navigate("/dashboard/products")}>
              {/* Top */}
              <div className="flex items-start justify-between mb-4">
                <div
                  className={`text-2xl w-12 h-12 rounded-xl ${c.bg} border ${c.border} flex items-center justify-center`}>
                  {cat.icon}
                </div>
                {locked ? (
                  <LockKeyhole size={15} className="text-gray-600" />
                ) : (
                  <ArrowRight
                    size={15}
                    className="text-gray-600 group-hover:text-gray-400 transition-colors"
                  />
                )}
              </div>

              {/* Info */}
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">
                {cat.name}
              </h3>
              <p className="text-xs text-gray-500 leading-relaxed mb-4">
                {cat.description}
              </p>

              {/* Products count */}
              <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-800">
                <div className="flex items-center gap-1.5 text-xs text-gray-400">
                  <Package size={13} className={c.icon} />
                  <span>
                    <span className="font-semibold text-gray-800 dark:text-gray-200">
                      {cat.count}
                    </span>{" "}
                    product{cat.count !== 1 ? "s" : ""}
                  </span>
                </div>
                {cat.count > 0 && (
                  <div className="flex items-center gap-1.5">
                    <div className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
                    <span className="text-[10px] text-gray-500">Active</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Usage summary */}
      <div className="card p-5">
        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-4">
          Category Distribution
        </h3>
        <div className="space-y-3">
          {categories.map((cat) => {
            const c = COLOR_MAP[cat.colorClass] || COLOR_MAP.purple;
            const total = categories.reduce((s, c2) => s + c2.count, 0);
            const pct = total > 0 ? Math.round((cat.count / total) * 100) : 0;
            return (
              <div key={cat.id} className="flex items-center gap-3">
                <span className="text-base w-6 text-center flex-shrink-0">
                  {cat.icon}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                      {cat.name}
                    </span>
                    <span className="text-xs text-gray-500">{cat.count}</span>
                  </div>
                  <div className="h-1.5 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-700 ${c.dot.replace("bg-", "bg-")}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
                <span className="text-xs text-gray-600 w-9 text-right flex-shrink-0">
                  {pct}%
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
